import { useState } from "react";
import { useApp } from "../context/AppContext";
import { useI18n } from "../context/I18nContext";

/**
 * Bandeau d'échéance d'abonnement.
 *
 * Il n'apparaît que dans la semaine précédant la fin de période, ou juste
 * après une expiration. Le reste du temps il n'existe pas : un bandeau
 * permanent devient un décor qu'on ne lit plus.
 *
 * Un abonné sans échéance (`perpetual`) n'en voit jamais — ce sont les
 * comptes antérieurs à la mise en place du renouvellement, volontairement
 * laissés intacts.
 */

const TEXTES = {
  fr: {
    expireDemain: "Votre abonnement {plan} expire demain.",
    expireJours: "Votre abonnement {plan} expire dans {n} jours.",
    expire: "Votre abonnement {plan} est arrivé à échéance.",
    apresExpiration: "Votre compte est repassé au plan gratuit. Vos données sont intactes.",
    renouveler: "Renouveler",
    fermer: "Masquer",
  },
  en: {
    expireDemain: "Your {plan} subscription expires tomorrow.",
    expireJours: "Your {plan} subscription expires in {n} days.",
    expire: "Your {plan} subscription has expired.",
    apresExpiration: "Your account is back on the free plan. Your data is untouched.",
    renouveler: "Renew",
    fermer: "Dismiss",
  },
  ar: {
    expireDemain: "ينتهي اشتراكك {plan} غداً.",
    expireJours: "ينتهي اشتراكك {plan} خلال {n} أيام.",
    expire: "انتهى اشتراكك {plan}.",
    apresExpiration: "عاد حسابك إلى الخطة المجانية. بياناتك سليمة.",
    renouveler: "تجديد",
    fermer: "إخفاء",
  },
  zh: {
    expireDemain: "您的 {plan} 订阅将于明天到期。",
    expireJours: "您的 {plan} 订阅将在 {n} 天后到期。",
    expire: "您的 {plan} 订阅已到期。",
    apresExpiration: "您的账户已回到免费计划。您的数据完好无损。",
    renouveler: "续订",
    fermer: "隐藏",
  },
};

// En deçà de ce seuil, on prévient. Au-delà, on se tait.
const SEUIL_JOURS = 7;

export default function SubscriptionBanner({ onRenew }) {
  const { user } = useApp();
  const { lang } = useI18n();
  const txt = TEXTES[lang] || TEXTES.fr;

  const sub = user?.subscription;
  // Masquage volontaire, valable pour la session seulement : à la prochaine
  // ouverture l'échéance approche encore, l'information reste due.
  const [masque, setMasque] = useState(() => {
    try { return sessionStorage.getItem("sub_banner_dismissed") === "1"; } catch { return false; }
  });

  if (!sub || sub.perpetual || masque) return null;

  const jours = sub.daysLeft;
  const expire = sub.expired || (typeof jours === "number" && jours <= 0);
  if (!expire && (typeof jours !== "number" || jours > SEUIL_JOURS)) return null;

  const plan = sub.plan || "";
  const message = expire
    ? txt.expire.replace("{plan}", plan)
    : jours === 1
      ? txt.expireDemain.replace("{plan}", plan)
      : txt.expireJours.replace("{plan}", plan).replace("{n}", String(jours));

  const urgent = expire || jours <= 1;

  return (
    <div
      className="subscription-banner"
      data-urgent={urgent ? "true" : "false"}
      role="status"
      aria-live="polite"
    >
      <span className="subscription-banner__icon" aria-hidden="true">{expire ? "🔔" : "⏳"}</span>
      <span className="subscription-banner__text">
        <strong>{message}</strong>
        {expire && <span className="subscription-banner__sub"> {txt.apresExpiration}</span>}
      </span>
      <button type="button" className="subscription-banner__cta" onClick={onRenew}>
        {txt.renouveler}
      </button>
      <button
        type="button"
        className="subscription-banner__close"
        aria-label={txt.fermer}
        onClick={() => {
          setMasque(true);
          try { sessionStorage.setItem("sub_banner_dismissed", "1"); } catch { /* mode privé */ }
        }}
      >
        ×
      </button>
    </div>
  );
}
